/**
 * Appointment Layout Utilities
 *
 * Provides functions for calculating the horizontal layout of overlapping appointments
 * so they appear side by side instead of stacked on top of each other.
 */

import type { Appointment } from 'pos-client/components/customScheduler/types/schedulerTypes';

export interface AppointmentLayout {
  column: number;
  totalColumns: number;
  hidden: boolean;
}

/**
 * Checks if two appointments overlap in time
 */
function appointmentsOverlap(
  appointment1: Appointment,
  appointment2: Appointment
): boolean {
  const start1 = new Date(appointment1.startDate);
  const end1 = new Date(appointment1.endDate);
  const start2 = new Date(appointment2.startDate);
  const end2 = new Date(appointment2.endDate);

  return start1 < end2 && end1 > start2;
}

/**
 * Calculates the layout for overlapping appointments within a time slot
 * Returns a map of appointment IDs to their layout information (column and total columns)
 */
export function calculateAppointmentLayouts(
  appointments: Appointment[],
  options?: { maxVisibleColumns?: number }
): Map<string | number, AppointmentLayout> {
  const layouts = new Map<string | number, AppointmentLayout>();

  if (appointments.length === 0) {
    return layouts;
  }

  // Determine effective cap: 0 or negative means no limit
  const maxVisible =
    options?.maxVisibleColumns && options.maxVisibleColumns > 0
      ? options.maxVisibleColumns
      : undefined;

  // Sort appointments by start time, then by duration (longer first)
  const sortedAppointments = [...appointments].sort((a, b) => {
    const startA = new Date(a.startDate).getTime();
    const startB = new Date(b.startDate).getTime();
    if (startA !== startB) {
      return startA - startB;
    }
    // If start times are equal, longer appointments come first
    const durationA =
      new Date(a.endDate).getTime() - new Date(a.startDate).getTime();
    const durationB =
      new Date(b.endDate).getTime() - new Date(b.startDate).getTime();
    return durationB - durationA;
  });

  // Build overlap groups
  const overlapGroups: Appointment[][] = [];

  for (const appointment of sortedAppointments) {
    // Find which group this appointment belongs to
    let addedToGroup = false;

    for (const group of overlapGroups) {
      // Check if appointment overlaps with any appointment in the group
      const overlapsWithGroup = group.some((groupApp) =>
        appointmentsOverlap(appointment, groupApp)
      );

      if (overlapsWithGroup) {
        group.push(appointment);
        addedToGroup = true;
        break;
      }
    }

    // If not added to any group, create a new group
    if (!addedToGroup) {
      overlapGroups.push([appointment]);
    }
  }

  // Assign columns within each overlap group
  for (const group of overlapGroups) {
    if (group.length === 1) {
      // Single appointment, takes full width
      const appointmentId = group[0].id;
      if (appointmentId !== undefined) {
        layouts.set(appointmentId, {
          column: 0,
          totalColumns: 1,
          hidden: false,
        });
      }
      continue;
    }

    // Multiple overlapping appointments - assign columns
    const columns: Appointment[][] = [];

    for (const appointment of group) {
      // Find the first column where this appointment doesn't overlap with existing appointments
      let columnIndex = 0;
      let placed = false;

      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }

        const overlapsInColumn = columns[columnIndex].some((colApp) =>
          appointmentsOverlap(appointment, colApp)
        );

        if (!overlapsInColumn) {
          columns[columnIndex].push(appointment);
          placed = true;
        } else {
          columnIndex++;
        }
      }
    }

    // Determine actual and effective column counts
    const actualColumns = columns.length;
    const effectiveTotalColumns =
      maxVisible !== undefined
        ? Math.min(actualColumns, maxVisible)
        : actualColumns;

    // Assign layout information
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const hidden = maxVisible !== undefined && colIndex >= maxVisible;

      for (const appointment of columns[colIndex]) {
        const appointmentId = appointment.id;
        if (appointmentId !== undefined) {
          layouts.set(appointmentId, {
            column: colIndex,
            totalColumns: effectiveTotalColumns,
            hidden,
          });
        }
      }
    }
  }

  return layouts;
}

/**
 * Gets the style for an appointment based on its layout
 */
export function getAppointmentLayoutStyle(
  layout: AppointmentLayout | undefined
): React.CSSProperties {
  if (!layout || layout.totalColumns === 1) {
    return {
      left: '2px',
      right: '2px',
    };
  }

  // 10px padding on the outer edges of the group (left of first column, right of last column).
  // 2px gap between adjacent columns.
  const cellPadding = 10;
  const gap = 2;
  const columnWidth = 100 / layout.totalColumns;
  const leftPercent = layout.column * columnWidth;

  const leftOffset = layout.column === 0 ? cellPadding : gap / 2;
  const rightOffset =
    layout.column === layout.totalColumns - 1 ? cellPadding : gap / 2;

  return {
    left: `calc(${leftPercent}% + ${leftOffset}px)`,
    width: `calc(${columnWidth}% - ${leftOffset + rightOffset}px)`,
    margin: '0px 0px 0px 0px',
  };
}
